import { useState, useMemo } from "react";

export interface Coding {
  id: number;
  aia_codage_id: string;
  aia_game_id: string;
  aia_game_name: string;
  aia_codage_type: string;
  // What the coder must produce: events_coding | events_labeling | full
  aia_codage_task_type: string | null;
  aia_codage_Date: string;
  aia_coder_id: string;
  coderId: number | null;
  gameId: number | null;
  contractId: number | null;
  factureId: number | null;
  coder?: {
    first_name: string;
    last_name: string;
    email: string;
  };
  game?: {
    gameName: string;
  };
  contract?: {
    id: number;
    contractStatus: string;
  };
  facture?: {
    payment_date: string;
    payment_ref: string;
  };
  metadata: any;
}

export interface CodingFilters {
  dateFrom: string;
  dateTo: string;
  coder: string;
  type: string;
  taskType: string;
  status: string;
  /** "true" to keep only codings whose match is imminent and that have no coder. */
  lateAssignment: string;
  sortBy: "date" | "coder" | "type" | "taskType" | "status";
  sortOrder: "asc" | "desc";
}

export const useCodingFilters = (
  codings: Coding[],
  initialFilters?: Partial<CodingFilters>,
) => {
  const [filters, setFilters] = useState<CodingFilters>({
    dateFrom: "",
    dateTo: "",
    coder: "",
    type: "",
    taskType: "",
    status: "",
    lateAssignment: "",
    sortBy: "date",
    sortOrder: "desc",
    ...initialFilters,
  });

  // Get unique values for filter options
  /**
   * Only sorts. Every filter is sent to the API by the page, which matches the
   * whole table — matching them again here would be redundant, and matching them
   * *only* here would restrict the result to whichever rows the current page
   * happens to hold.
   */
  /**
   * The rows exactly as the API returned them.
   *
   * Neither filtered nor sorted here: the page sends both to the API, which applies
   * them across the whole table. Sorting locally reordered only the rows already
   * loaded — 25 of several thousand — while the sortable headers implied the whole
   * table had been sorted.
   */
  const filteredCodings = useMemo(() => codings, [codings]);

  // Update filter functions
  const updateFilter = (key: keyof CodingFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateSort = (sortBy: CodingFilters["sortBy"]) => {
    setFilters((prev) => ({
      ...prev,
      sortBy,
      sortOrder:
        prev.sortBy === sortBy && prev.sortOrder === "asc" ? "desc" : "asc",
    }));
  };

  const resetFilters = () => {
    setFilters({
      dateFrom: "",
      dateTo: "",
      coder: "",
      type: "",
      taskType: "",
      status: "",
      lateAssignment: "",
      sortBy: "date",
      sortOrder: "desc",
    });
  };

  const hasActiveFilters = () => {
    return !!(
      filters.dateFrom ||
      filters.dateTo ||
      filters.coder ||
      filters.type ||
      filters.taskType ||
      filters.status ||
      filters.lateAssignment
    );
  };

  return {
    filters,
    filteredCodings,
    updateFilter,
    updateSort,
    resetFilters,
    hasActiveFilters: hasActiveFilters(),
  };
};
